import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const popularCats = categories.slice(0, 7);
  const moreCats = categories.slice(7);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormMessage('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Thank you! You have been subscribed.');
      setEmail('');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d95q7m3jjt5fme3c519g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you! You have been subscribed.');
        setEmail('');
      } else if (typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam'))) {
        setFormStatus('error');
        setFormMessage('Submission could not be processed. Please try again.');
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50/70">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-lg font-heading font-bold text-background-50 tracking-tight">
                CSSSpreadsheet<span className="text-primary-400">.</span>study
              </span>
            </Link>
            <p className="text-xs md:text-sm leading-relaxed mb-4">
              The ultimate CSSBuy Spreadsheet hub — your one-stop destination for curated finds, expert buying guides, and the most comprehensive spreadsheet collection across 14 product categories.
            </p>
          </div>

          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-background-50/40 mb-5">
              Navigation
            </h5>
            <div className="space-y-3">
              <Link to="/" className="block text-sm text-background-50/70 hover:text-background-50 transition-colors">Home</Link>
              <Link to="/article/cssbuy-sheet-complete-guide" className="block text-sm text-background-50/70 hover:text-background-50 transition-colors">Buying Guide</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block text-sm text-background-50/70 hover:text-background-50 transition-colors">kaspreadsheet.com</a>
              <Link to="/" className="block text-sm text-background-50/70 hover:text-background-50 transition-colors">Categories</Link>
            </div>
          </div>

          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-background-50/40 mb-5">
              Popular Categories
            </h5>
            <div className="space-y-3">
              {popularCats.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block text-sm text-background-50/70 hover:text-background-50 transition-colors"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-background-50/40 mb-5">
              More Categories
            </h5>
            <div className="space-y-3 mb-6">
              {moreCats.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block text-sm text-background-50/70 hover:text-background-50 transition-colors"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-50/10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wider text-background-50/40 mb-3">
                Newsletter
              </h5>
              <p className="text-xs text-background-50/40 mb-4 max-w-sm">
                Get the latest CSSBuy spreadsheet updates and new finds delivered to your inbox.
              </p>
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex items-center gap-0 max-w-sm"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-protection-field"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-transparent border-b border-background-50/20 text-sm text-background-50 py-2 px-0 outline-none focus:border-primary-400 transition-colors placeholder:text-background-50/30 min-w-0"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-9 h-9 rounded-full bg-primary-500 text-foreground-950 flex items-center justify-center hover:bg-primary-400 transition-colors shrink-0 cursor-pointer ml-2 disabled:opacity-50"
                >
                  <i className={`${formStatus === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} w-4 h-4 flex items-center justify-center`}></i>
                </button>
              </form>
              {formMessage && (
                <p className={`text-xs mt-2 ${formStatus === 'success' ? 'text-primary-400' : 'text-accent-400'}`}>
                  {formMessage}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-xs text-background-50/30">
            &copy; {new Date().getFullYear()} CSSSpreadsheet.study — All external links open on kaspreadsheet.com
          </p>
        </div>
      </div>

      <div className="bg-foreground-950 overflow-hidden">
        <div className="text-[10vw] md:text-[8vw] font-heading font-bold text-background-50/[0.03] text-center leading-none py-6 select-none tracking-tighter whitespace-nowrap">
          CSSBUY SPREADSHEET
        </div>
      </div>
    </footer>
  );
}